import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, Kpi, Bars, Pager, num, dt } from "./crmui";
import { toast } from "sonner";
import { Plus, Timer } from "lucide-react";

const TABS = ["Overview", "Accounts", "Ledger", "Tiers", "Earning Rules", "Redemption Rules"];

const FORMS = {
  tiers: [["name", "Tier name", "text"], ["min_points", "Qualification points", "number"],
          ["earn_multiplier", "Earn multiplier", "number"], ["qualification_days", "Qualification window (days, blank = lifetime)", "number"],
          ["benefits_text", "Benefits (comma separated)", "text"]],
  rules: [["name", "Rule name", "text"], ["type", "Type (amount/product/promo)", "text"],
          ["amount_per_point", "Spend per point (₦)", "number"], ["points", "Points awarded", "number"],
          ["multiplier", "Multiplier", "number"]],
  "redemption-rules": [["name", "Rule name", "text"], ["min_points", "Minimum points", "number"],
                        ["max_points_per_txn", "Maximum points per transaction", "number"],
                        ["point_value", "Value per point (₦)", "number"]],
};

export default function Loyalty() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [d, setD] = useState(null);
  const [accounts, setAccounts] = useState({ rows: [], total: 0 });
  const [ledger, setLedger] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(1);
  const [config, setConfig] = useState({ tiers: [], rules: [], "redemption-rules": [] });
  const [edit, setEdit] = useState(null);

  const loadConfig = useCallback(() => {
    ["tiers", "rules", "redemption-rules"].forEach((k) =>
      api.get(`/crm/loyalty/${k}`).then((r) => setConfig((c) => ({ ...c, [k]: r }))).catch(() => {}));
  }, []);

  useEffect(() => { api.get("/crm/loyalty/dashboard").then(setD).catch((e) => toast.error(formatApiError(e))); loadConfig(); }, [loadConfig]);
  useEffect(() => {
    if (tab === "Accounts") api.get("/crm/loyalty/accounts", { params: { page, page_size: 50 } }).then(setAccounts).catch(() => {});
    if (tab === "Ledger") api.get("/crm/loyalty/transactions", { params: { page, page_size: 50 } }).then(setLedger).catch(() => {});
  }, [tab, page]);

  const kind = tab === "Tiers" ? "tiers" : tab === "Earning Rules" ? "rules" : "redemption-rules";

  const save = async () => {
    const payload = { ...edit };
    if (kind === "tiers") {
      payload.benefits = (payload.benefits_text || "").split(",").map((s) => s.trim()).filter(Boolean);
      delete payload.benefits_text;
      if (payload.qualification_days === "") payload.qualification_days = null;
    }
    ["min_points", "earn_multiplier", "points", "amount_per_point", "multiplier",
     "max_points_per_txn", "point_value", "qualification_days"].forEach((f) => {
      if (payload[f] !== undefined && payload[f] !== null && payload[f] !== "") payload[f] = Number(payload[f]);
    });
    try {
      if (payload.id) await api.put(`/crm/loyalty/${kind}/${payload.id}`, payload);
      else await api.post(`/crm/loyalty/${kind}`, payload);
      toast.success("Saved"); setEdit(null); loadConfig();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-loyalty-page">
      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => { setTab(t); setPage(1); }} data-testid={`loyalty-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className="h-9 px-4 rounded-full text-[12.5px] font-semibold border"
            style={tab === t ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy } : { background: "#fff", color: HC.text, borderColor: HC.border }}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && d && (
        <div className="space-y-4" data-testid="loyalty-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testid="ld-members" label="Loyalty members" value={num(d.members)} />
            <Kpi testid="ld-active" label="Active members" value={num(d.active_members)} tone="green" />
            <Kpi testid="ld-pending" label="Awaiting verification" value={num(d.pending_members)} tone="amber" />
            <Kpi testid="ld-issued" label="Points issued" value={num(d.points_issued)} />
            <Kpi testid="ld-redeemed" label="Points redeemed" value={num(d.points_redeemed)} />
            <Kpi testid="ld-expired" label="Points expired" value={num(d.points_expired)} />
            <Kpi testid="ld-outstanding" label="Outstanding points" value={num(d.points_outstanding)} tone="gold" />
            <Kpi testid="ld-pending-points" label="Pending points" value={num(d.points_pending)} tone="amber" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Bars testid="ld-by-tier" title="Members by tier" rows={d.by_tier} />
            <DataTable testid="ld-tier-movement" rows={d.tier_movement}
              columns={[["at", "When"], ["from_tier", "From"], ["to_tier", "To"], ["direction", "Movement"]]}
              render={{ at: (r) => dt(r.at) }} empty="No tier movements yet" />
          </div>
          <Btn variant="outline" data-testid="loyalty-expire-now"
            onClick={async () => {
              try { const r = await api.post("/crm/loyalty/expire-now", {}); toast.success(`${r.points} points expired across ${r.expired_customers} wallets`); }
              catch (e) { toast.error(formatApiError(e)); }
            }}><Timer size={14} /> Run points expiry now</Btn>
        </div>
      )}

      {tab === "Accounts" && (
        <>
          <DataTable testid="loyalty-accounts-table" rows={accounts.rows}
            columns={[["name", "Customer"], ["loyalty_tier", "Tier"], ["points_available", "Available"],
                      ["points_pending", "Pending"], ["wallet_active", "Wallet"], ["orders", "Orders"], ["last_purchase", "Last purchase"]]}
            render={{
              points_available: (r) => num(r.points_available), points_pending: (r) => num(r.points_pending),
              wallet_active: (r) => <Pill status={r.wallet_active ? "active" : "pending"} text={r.wallet_active ? "Active" : "Pending"} />,
              last_purchase: (r) => dt(r.last_purchase),
            }}
            onRowClick={(r) => navigate(`/crm/customers/${r.id}`)} empty="No loyalty accounts yet" />
          <Pager page={page} pageSize={50} total={accounts.total} onPage={setPage} testid="loyalty-accounts-pager" />
        </>
      )}

      {tab === "Ledger" && (
        <>
          <DataTable testid="loyalty-ledger-table" rows={ledger.rows}
            columns={[["txn_no", "Transaction"], ["created_at", "Date"], ["customer_name", "Customer"], ["type", "Type"],
                      ["points", "Points"], ["previous_balance", "Previous"], ["new_balance", "New"],
                      ["source", "Source"], ["order_number", "Order"], ["reason", "Reason"], ["user_name", "By"]]}
            render={{ created_at: (r) => dt(r.created_at), type: (r) => <Pill status={r.type === "redeem" ? "pending" : "approved"} text={r.type} /> }}
            empty="No loyalty transactions yet" />
          <Pager page={page} pageSize={50} total={ledger.total} onPage={setPage} testid="loyalty-ledger-pager" />
        </>
      )}

      {["Tiers", "Earning Rules", "Redemption Rules"].includes(tab) && (
        <>
          <div className="flex justify-end">
            <Btn onClick={() => setEdit(kind === "rules" ? { type: "amount", status: "active" } : { status: "active" })}
              data-testid={`loyalty-new-${kind}`}><Plus size={14} /> New</Btn>
          </div>
          <DataTable testid={`loyalty-${kind}-table`} rows={config[kind]}
            columns={kind === "tiers"
              ? [["name", "Tier"], ["min_points", "Qualification"], ["earn_multiplier", "Multiplier"],
                 ["qualification_days", "Window (days)"], ["benefits", "Benefits"], ["status", "Status"]]
              : kind === "rules"
                ? [["name", "Rule"], ["type", "Type"], ["amount_per_point", "Spend per point"], ["points", "Points"],
                   ["multiplier", "Multiplier"], ["status", "Status"]]
                : [["name", "Rule"], ["min_points", "Min points"], ["max_points_per_txn", "Max per txn"],
                   ["point_value", "₦ per point"], ["status", "Status"]]}
            render={{
              benefits: (r) => (r.benefits || []).join(", ") || "—",
              status: (r) => <Pill status={r.status === "active" ? "active" : "draft"} text={r.status} />,
              qualification_days: (r) => r.qualification_days || "Lifetime",
            }}
            onRowClick={(r) => setEdit({ ...r, benefits_text: (r.benefits || []).join(", ") })}
            empty="Nothing configured" />
        </>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-md p-5 space-y-3" style={{ borderColor: HC.border }} data-testid="loyalty-config-modal">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>{edit.id ? "Edit" : "New"} {tab.replace(/s$/, "")}</h3>
            {(FORMS[kind] || []).map(([f, label, type]) => (
              <label key={f} className="block">
                <span className="block text-[10px] font-bold tracking-[0.14em] mb-1" style={{ color: HC.soft }}>{label.toUpperCase()}</span>
                <input type={type} value={edit[f] ?? ""} data-testid={`loyalty-field-${f}`}
                  onChange={(e) => setEdit({ ...edit, [f]: e.target.value })}
                  className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />
              </label>
            ))}
            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn onClick={save} data-testid="loyalty-config-save">Save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
